import { throttle } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CircularProgress } from './CircularProgress';

export const InfiniteScrollPagination = ({
  fetchData,
  isFetching,
  hasMore = false,
  loading = false,
  heightPercent = 78,
  threshold = 0.5,
  children,
  overrideStyles = null,
}) => {
  const containerRef = useRef(null);
  const loaderRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(() => Math.floor(window.innerHeight * (heightPercent / 100)));

  // Update height on window resize
  useEffect(() => {
    const handleResize = () => {
      setContainerHeight(Math.floor(window.innerHeight * (heightPercent / 100)));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [heightPercent]);

  // Throttled fetch to avoid spamming
  const throttledFetchData = useMemo(() => {
    return throttle(() => {
      if (!isFetching && !loading && hasMore) {
        fetchData();
      }
    }, 500);
  }, [fetchData, isFetching, loading, hasMore]);

  //  Observer to trigger fetch when loader is visible
  useEffect(() => {
    const container = containerRef.current;
    const loader = loaderRef.current;
    if (!container || !loader) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only trigger if visible, not already fetching, and hasn't been triggered yet
        if (entry.isIntersecting && !isFetching && hasMore) {
          throttledFetchData();
        }
      },
      {
        root: container,
        rootMargin: '0px',
        threshold,
      }
    );

    observer.observe(loader);

    return () => {
      observer.disconnect();
      throttledFetchData.cancel(); // Cancel any pending debounced calls
    };
  }, [throttledFetchData, threshold, containerRef, loaderRef, isFetching, hasMore]);

  return (
    <div
      ref={containerRef}
      style={overrideStyles ? overrideStyles : { height: containerHeight, overflowY: 'auto' }}
    >
      {/* The children prop is used to render the content that will be scrolled */}
      {children}
      {/* The loaderRef is used to detect when the user has scrolled near the bottom of the page */}
      {/* This div acts as a sentinel for triggering the fetchData function */}
      <div
        ref={loaderRef}
        style={{ height: 2 }}
      />
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
        {loading && <CircularProgress />}
      </div>
    </div>
  );
};
